/**
 * Dashboard API client. Bearer token persisted in localStorage so a refresh
 * doesn't kick the accountant back to login. Stage-2 swaps to httpOnly
 * cookies + CSRF; this is the L1 dev shape.
 */

const API_BASE: string =
  (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  "https://api.dovach.local:8080";
const TOKEN_KEY = "dovach.access_token";
const REFRESH_KEY = "dovach.refresh_token";

export class ApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`API ${status}: ${detail}`);
  }
}

interface StoredTokens {
  access_token: string;
  refresh_token: string;
}

export const auth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (t: string | null) =>
    t === null ? localStorage.removeItem(TOKEN_KEY) : localStorage.setItem(TOKEN_KEY, t),
  getRefreshToken: () => localStorage.getItem(REFRESH_KEY),
  setRefreshToken: (t: string | null) =>
    t === null
      ? localStorage.removeItem(REFRESH_KEY)
      : localStorage.setItem(REFRESH_KEY, t),
  /** Persist both tokens at login / 2FA / refresh. Passing null clears
   *  both, used on logout + on terminal refresh failure. */
  setTokens: (tokens: StoredTokens | null) => {
    if (tokens === null) {
      auth.setToken(null);
      auth.setRefreshToken(null);
    } else {
      auth.setToken(tokens.access_token);
      auth.setRefreshToken(tokens.refresh_token);
    }
  },
};

/** Single-flight refresh promise. If N concurrent requests all 401 with
 *  `access_token_expired`, only ONE `/v1/auth/refresh` actually fires;
 *  the others await the same promise. Otherwise the 2nd refresh would
 *  fail with `refresh_token_reused` because the 1st rotated the token,
 *  and the API treats reuse as a stolen-token signal that revokes the
 *  whole session family. */
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const refresh = auth.getRefreshToken();
  if (!refresh) return false;
  refreshInFlight = (async () => {
    try {
      const resp = await fetch(API_BASE + "/v1/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ refresh_token: refresh }),
        credentials: "omit",
      });
      if (!resp.ok) {
        auth.setTokens(null);
        return false;
      }
      const tokens = (await resp.json()) as StoredTokens;
      auth.setTokens(tokens);
      return true;
    } catch {
      auth.setTokens(null);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const doFetch = async (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = auth.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    let payload: BodyInit | undefined;
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
    return fetch(API_BASE + path, {
      method, headers, body: payload, credentials: "omit",
    });
  };

  let resp = await doFetch();
  if (resp.status === 401) {
    // Read detail off a clone so the original Response is preserved for
    // the error-throw path if refresh doesn't help.
    let detail = "";
    try {
      const j = (await resp.clone().json()) as { detail?: string };
      detail = j.detail ?? "";
    } catch {
      /* ignore */
    }
    if (detail === "access_token_expired" && (await tryRefreshAccessToken())) {
      resp = await doFetch();
    }
  }
  if (!resp.ok) {
    let detail = `http_${resp.status}`;
    try {
      const j = await resp.json();
      if (j && typeof j.detail === "string") detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(resp.status, detail);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

// --------------------------------------------------- types

export interface TokenPair {
  access_token: string;
  refresh_token: string;
}

export interface LoginResponse {
  tokens: TokenPair | null;
  needs_2fa: boolean;
  partial_token: string | null;
}

export interface MeResponse {
  account_id: string;
  email: string;
  display_name: string;
  has_2fa: boolean;
}

export interface ClientResponse {
  client_id: string;
  name: string;
  phone_e164: string | null;
  email?: string | null;
  reminder_schedule_days?: number[] | null;
  expected_payment_minor?: number | null;
  reminders_paused?: boolean;
}

export interface CampaignResponse {
  campaign_id: string;
  name: string;
  document_list: string[];
  /** Per-campaign reminder template. NULL = fall through to client
   *  setting → global default. */
  default_reminder_schedule_days?: number[] | null;
  /** Default deadline-in-days pre-populated when starting a new run
   *  for this campaign. */
  default_deadline_days?: number | null;
}

export interface RunResponse {
  run_id: string;
  campaign_id: string;
  period_label: string;
  deadline_at: string;
  /** Set when the accountant closed the run. null = still active. */
  closed_at?: string | null;
}

export interface InviteCreatedResponse {
  invite_id: string;
  invite_url: string;
  expires_at: string;
  /** True when the invite email landed at the client's inbox. False
   *  means the dashboard should show the URL for manual forwarding. */
  email_delivered?: boolean;
  /** Address the invite was emailed to. null when delivery was skipped
   *  or failed. */
  email_recipient?: string | null;
  /** 'no_email' = client has no email on file; 'send_failed' = SMTP
   *  refused. null = delivered successfully. */
  delivery_skipped_reason?: "no_email" | "send_failed" | null;
}

export interface InboxResponse {
  run_id: string;
  rows: { document_label: string; count: number }[];
  total_files: number;
  total_bytes: number;
}

export type FileStatus =
  | "uploaded"
  | "pulled"
  | "acked"
  | "pending_review"
  | "approved"
  | "rejected"
  | "archived";

export interface InboxFile {
  file_id: string;
  client_id: string;
  client_name: string | null;
  document_label: string | null;
  size_bytes: number;
  uploaded_at: string;
  pulled_at: string | null;
  acked_at: string | null;
  archived_at: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  status: FileStatus;
}

export interface InboxFilesResponse {
  run_id: string;
  files: InboxFile[];
}

export interface ClientsListResponse {
  clients: ClientResponse[];
}

export interface CampaignsListResponse {
  campaigns: CampaignResponse[];
}

export interface RunsListResponse {
  runs: RunResponse[];
}

export interface AuditResponse {
  rows: {
    seq: number;
    ts: string;
    action: string;
    actor: string;
    resource_id: string | null;
    prev_hash: string;
    this_hash: string;
  }[];
  chain_verified: boolean;
}

/** Cascade preview for the "are you sure" delete modal — counts of
 *  child rows that would be removed by a hard delete on the parent. */
export interface CascadeUsageResponse {
  runs: number;
  invites: number;
  files: number;
  bytes_total: number;
}

/** Outcome of a hard-delete cascade — counts of rows actually removed. */
export interface CascadeDeleteResponse {
  runs_deleted: number;
  invites_deleted: number;
  files_deleted: number;
  messages_sent_deleted: number;
  /** S3 blobs successfully purged after the DB commit. May be less
   *  than `files_deleted` if some blobs were already gone or transient
   *  storage errors prevented deletion — the DB row went either way. */
  s3_keys_deleted: number;
}

// --------------------------------------------------- public

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>("POST", "/v1/auth/login", { email, password }),
  me: () => request<MeResponse>("GET", "/v1/auth/me"),
  logout: () => {
    // Best-effort server-side revoke. If there's no stored refresh token
    // (already logged out, never logged in) skip the call — the API
    // rejects refresh_token shorter than 20 chars anyway.
    const rt = auth.getRefreshToken();
    if (!rt) return Promise.resolve();
    return request<void>("POST", "/v1/auth/logout", { refresh_token: rt }).catch(() => {});
  },
  changePassword: (current_password: string, new_password: string) =>
    request<{ sessions_revoked: number }>(
      "POST",
      "/v1/auth/change-password",
      { current_password, new_password },
    ),
  forgotPassword: (email: string, turnstile_token?: string | null) =>
    request<{ sent: boolean }>("POST", "/v1/auth/forgot-password", {
      email,
      turnstile_token: turnstile_token ?? null,
    }),
  resetPasswordViaToken: (token: string, new_password: string) =>
    request<{ ok: boolean }>("POST", "/v1/auth/reset-password", {
      token,
      new_password,
    }),
  signup: (
    email: string,
    password: string,
    display_name: string,
    turnstile_token?: string | null,
  ) =>
    request<{
      account_id: string;
      email_verification_required: boolean;
      auto_approved: boolean;
    }>("POST", "/v1/auth/signup", {
      email,
      password,
      display_name,
      turnstile_token: turnstile_token ?? null,
    }),
  verifyEmail: (token: string) =>
    request<{ ok: boolean; awaiting_operator_approval: boolean }>(
      "POST",
      "/v1/auth/verify-email",
      { token },
    ),

  clientCreate: (
    name: string,
    phone_e164: string | null,
    email: string | null = null,
  ) =>
    request<ClientResponse>("POST", "/v1/clients", { name, phone_e164, email }),
  campaignCreate: (name: string, document_list: string[]) =>
    request<CampaignResponse>("POST", "/v1/campaigns", { name, document_list }),
  campaignSetDefaults: (
    campaign_id: string,
    body: {
      default_reminder_schedule_days: number[] | null;
      default_deadline_days: number | null;
    },
  ) =>
    request<CampaignResponse>(
      "PATCH",
      `/v1/campaigns/${campaign_id}/defaults`,
      body,
    ),
  runCreate: (campaign_id: string, period_label: string, deadline_in_days: number) =>
    request<RunResponse>("POST", "/v1/runs", { campaign_id, period_label, deadline_in_days }),
  inviteCreate: (run_id: string, client_id: string) =>
    request<InviteCreatedResponse>("POST", "/v1/invites", { run_id, client_id }),

  inviteBulk: (run_id: string, client_ids: string[]) =>
    request<{
      invites: InviteCreatedResponse[];
      failures: { client_id: string; error: string }[];
    }>("POST", "/v1/invites/bulk", { run_id, client_ids }),

  clientGet: (client_id: string) =>
    request<ClientResponse>("GET", `/v1/clients/${client_id}`),

  clientUpdate: (
    client_id: string,
    patch: { name?: string; phone_e164?: string | null; email?: string | null },
  ) => request<ClientResponse>("PATCH", `/v1/clients/${client_id}`, patch),

  clientDelete: (client_id: string) =>
    request<void>("DELETE", `/v1/clients/${client_id}`),

  /** Cascade preview: how many invites/files/bytes would die with this client. */
  clientUsage: (client_id: string) =>
    request<CascadeUsageResponse>("GET", `/v1/clients/${client_id}/usage`),

  /** Hard-delete: client + all their invites, files, S3 blobs, OTPs, etc. */
  clientDeletePermanent: (client_id: string) =>
    request<CascadeDeleteResponse>(
      "DELETE", `/v1/clients/${client_id}/permanent`,
    ),

  /** Patch a campaign's name / document list. */
  campaignUpdate: (
    campaign_id: string,
    patch: { name?: string; document_list?: string[] },
  ) =>
    request<CampaignResponse>("PATCH", `/v1/campaigns/${campaign_id}`, patch),

  /** Cascade preview for the "delete campaign" modal. */
  campaignUsage: (campaign_id: string) =>
    request<CascadeUsageResponse>("GET", `/v1/campaigns/${campaign_id}/usage`),

  /** Hard-delete a campaign + runs + invites + files + S3 blobs. */
  campaignDelete: (campaign_id: string) =>
    request<CascadeDeleteResponse>("DELETE", `/v1/campaigns/${campaign_id}`),

  /** Cascade preview for the "delete run" modal. */
  runUsage: (run_id: string) =>
    request<CascadeUsageResponse>("GET", `/v1/runs/${run_id}/usage`),

  /** Hard-delete one run + its invites + files + S3 blobs. */
  runDelete: (run_id: string) =>
    request<CascadeDeleteResponse>("DELETE", `/v1/runs/${run_id}`),

  /** Un-invite a client from a run (drops their invite + uploaded files). */
  inviteDelete: (invite_id: string) =>
    request<CascadeDeleteResponse>("DELETE", `/v1/invites/${invite_id}`),

  /** Remove a client from a run while keeping their already-uploaded files. */
  inviteRemoveSoft: (invite_id: string) =>
    request<{ ok: boolean }>("DELETE", `/v1/invites/${invite_id}/remove`),

  /** Re-send the invite email to the client. */
  inviteResend: (invite_id: string) =>
    request<{
      email_delivered: boolean;
      delivery_skipped_reason: string | null;
      invite_url: string;
    }>("POST", `/v1/invites/${invite_id}/resend`),

  /** Get the live invite URL for copy-to-clipboard. */
  inviteUrl: (invite_id: string) =>
    request<{
      invite_url: string;
      expires_at: string;
      email: string | null;
    }>("GET", `/v1/invites/${invite_id}/url`),
  inbox: (run_id: string) => request<InboxResponse>("GET", `/v1/inbox?run_id=${run_id}`),

  inboxFiles: (run_id: string, includeArchived = false) =>
    request<InboxFilesResponse>(
      "GET",
      `/v1/inbox/files?run_id=${run_id}&include_archived=${includeArchived}`,
    ),

  archiveFile: (file_id: string) =>
    request<{ file_id: string; archived: boolean }>(
      "POST",
      `/v1/inbox/${file_id}/archive`,
    ),
  bulkApproveFiles: (file_ids: string[]) =>
    request<{
      succeeded: number;
      failed: number;
      rows: { file_id: string; status: string; ok: boolean }[];
    }>("POST", "/v1/inbox/approve-bulk", { file_ids }),
  bulkArchiveFiles: (file_ids: string[]) =>
    request<{
      succeeded: number;
      failed: number;
      rows: { file_id: string; status: string; ok: boolean }[];
    }>("POST", "/v1/inbox/archive-bulk", { file_ids }),

  // ----- approve / reject (Wave 8 collection workflow)
  approveFile: (file_id: string) =>
    request<{ file_id: string; status: FileStatus }>(
      "POST",
      `/v1/inbox/${file_id}/approve`,
    ),
  rejectFile: (file_id: string, reason: string) =>
    request<{ file_id: string; status: FileStatus }>(
      "POST",
      `/v1/inbox/${file_id}/reject`,
      { reason },
    ),

  fileDownloadUrl: (file_id: string) =>
    request<{ url: string }>("GET", `/v1/inbox/${file_id}/download-url`),

  // ----- per-client payment + custom reminder schedule
  clientSetPayment: (client_id: string, expected_payment_minor: number | null) =>
    request<ClientResponse>(
      "PATCH",
      `/v1/clients/${client_id}/payment`,
      { expected_payment_minor },
    ),
  clientSetReminderSchedule: (client_id: string, days: number[] | null) =>
    request<ClientResponse>(
      "PATCH",
      `/v1/clients/${client_id}/reminder-schedule`,
      { days },
    ),

  // ----- per-invite extra required docs (floating toolkit)
  clientSetExtraDocsAllRuns: (client_id: string, extra_docs: string[]) =>
    request<{ ok: boolean; updated_count: number }>(
      "POST",
      `/v1/clients/${client_id}/extra-docs`,
      { extra_docs },
    ),

  inviteSetExtraDocs: (invite_id: string, extra_required_docs: string[]) =>
    request<{ invite_id: string; extra_required_docs: string[] }>(
      "PATCH",
      `/v1/invites/${invite_id}/extra-docs`,
      { extra_required_docs },
    ),

  // ----- per-invite reminder overrides (per-run, per-client)
  inviteSetRemindersPaused: (invite_id: string, paused: boolean) =>
    request<{
      invite_id: string;
      reminders_paused: boolean;
      reminder_schedule_days: number[] | null;
    }>(
      "PATCH",
      `/v1/invites/${invite_id}/reminders-paused`,
      { paused },
    ),
  inviteSetReminderSchedule: (invite_id: string, days: number[] | null) =>
    request<{
      invite_id: string;
      reminders_paused: boolean;
      reminder_schedule_days: number[] | null;
    }>(
      "PATCH",
      `/v1/invites/${invite_id}/reminder-schedule`,
      { days },
    ),

  // ----- per-(run, client) workspace (kanban + toolkit)
  runClientCollection: (run_id: string, client_id: string) =>
    request<{
      run_id: string;
      client_id: string;
      client_name: string;
      client_phone: string | null;
      /** Client-level reminders_paused (global across all runs). */
      reminders_paused: boolean;
      expected_payment_minor: number | null;
      /** Client-level reminder_schedule_days (global). */
      reminder_schedule_days: number[] | null;
      invite_id: string | null;
      fingerprint: string | null;
      required_docs: string[];
      base_required_docs: string[];
      extra_required_docs: string[];
      files: {
        file_id: string;
        document_label: string | null;
        size_bytes: number;
        status: FileStatus;
        uploaded_at: string;
        rejection_reason: string | null;
        approved_at: string | null;
      }[];
      /** Per-(run, client) override of reminders_paused. False = fall
       *  through to client-level. */
      invite_reminders_paused?: boolean;
      /** Per-(run, client) override of reminder_schedule_days. null =
       *  fall through to client-level. */
      invite_reminder_schedule_days?: number[] | null;
    }>("GET", `/v1/runs/${run_id}/clients/${client_id}/collection`),

  clientsList: () => request<ClientsListResponse>("GET", "/v1/clients"),
  campaignsList: () => request<CampaignsListResponse>("GET", "/v1/campaigns"),
  runsList: (includeClosed = false) =>
    request<RunsListResponse>(
      "GET",
      `/v1/runs${includeClosed ? "?include_closed=true" : ""}`,
    ),
  closeRun: (run_id: string) =>
    request<RunResponse>("POST", `/v1/runs/${run_id}/close`),
  reopenRun: (run_id: string) =>
    request<RunResponse>("POST", `/v1/runs/${run_id}/reopen`),

  audit: (limit = 50) => request<AuditResponse>("GET", `/v1/audit?limit=${limit}`),

  // ----- 2FA
  twoFAStatus: () => request<{ enabled: boolean }>("GET", "/v1/auth/2fa/status"),
  twoFAEnroll: () =>
    request<{ otpauth_uri: string; secret_base32: string }>(
      "POST",
      "/v1/auth/2fa/enroll",
    ),
  twoFAConfirm: (code: string) =>
    request<{ enabled: boolean }>("POST", "/v1/auth/2fa/confirm", { code }),
  twoFADisable: (password: string, code: string) =>
    request<{ enabled: boolean }>("POST", "/v1/auth/2fa/disable", {
      password,
      code,
    }),
  twoFAVerifyLogin: (partial_token: string, code: string) =>
    request<TokenPair>("POST", "/v1/auth/2fa/verify", { partial_token, code }),

  // ----- /settings/agent
  agentSettings: () =>
    request<{
      auto_pull: boolean;
      base_path: string;
      default_path_pattern: string;
      default_filename_pattern: string;
      working_window: string | null;
    }>("GET", "/v1/settings/agent"),
  agentSettingsPatch: (patch: {
    auto_pull?: boolean;
    base_path?: string;
    default_path_pattern?: string;
    default_filename_pattern?: string;
  }) =>
    request<{
      auto_pull: boolean;
      base_path: string;
      default_path_pattern: string;
      default_filename_pattern: string;
      working_window: string | null;
    }>("PATCH", "/v1/settings/agent", patch),
  previewPath: (req: {
    client_name: string;
    run_label: string;
    document_label: string;
    ext?: string;
    date?: string;
    path_pattern_override?: string;
    filename_pattern_override?: string;
  }) =>
    request<{ resolved_path: string; resolved_filename: string }>(
      "POST",
      "/v1/settings/agent/preview-path",
      req,
    ),

  // ----- /settings/working-hours
  workingHours: () =>
    request<{ working_window: string | null }>(
      "GET",
      "/v1/settings/working-hours",
    ),
  workingHoursPatch: (working_window: string | null) =>
    request<{ working_window: string | null }>(
      "PATCH",
      "/v1/settings/working-hours",
      { working_window },
    ),

  // ----- /document-templates
  templatesList: () =>
    request<{
      templates: {
        template_id: string;
        name: string;
        documents: string[];
        created_at: string;
        updated_at: string;
      }[];
    }>("GET", "/v1/document-templates"),
  templateCreate: (name: string, documents: string[]) =>
    request<{
      template_id: string;
      name: string;
      documents: string[];
      created_at: string;
      updated_at: string;
    }>("POST", "/v1/document-templates", { name, documents }),
  templateUpdate: (id: string, patch: { name?: string; documents?: string[] }) =>
    request<{
      template_id: string;
      name: string;
      documents: string[];
      created_at: string;
      updated_at: string;
    }>("PATCH", `/v1/document-templates/${id}`, patch),
  templateDelete: (id: string) =>
    request<void>("DELETE", `/v1/document-templates/${id}`),

  // ----- /clients/:id drill-down
  clientFiles: (id: string) =>
    request<{
      files: {
        file_id: string;
        run_id: string;
        document_label: string | null;
        size_bytes: number;
        uploaded_at: string;
        pulled_at: string | null;
        acked_at: string | null;
        archived_at: string | null;
        status: string;
      }[];
    }>("GET", `/v1/clients/${id}/files`),
  clientRuns: (id: string) =>
    request<{
      runs: {
        run_id: string;
        campaign_id: string;
        period_label: string;
        deadline_at: string;
      }[];
    }>("GET", `/v1/clients/${id}/runs`),
  clientNotes: (id: string) =>
    request<{
      notes: { note_id: string; body: string; created_at: string }[];
    }>("GET", `/v1/clients/${id}/notes`),
  clientNoteCreate: (id: string, body: string) =>
    request<{ note_id: string; body: string; created_at: string }>(
      "POST",
      `/v1/clients/${id}/notes`,
      { body },
    ),
  clientActivity: (id: string) =>
    request<{
      total_files: number;
      total_bytes: number;
      last_file_uploaded_at: string | null;
      runs_count: number;
      notes_count: number;
    }>("GET", `/v1/clients/${id}/activity`),

  // ----- 2FA recovery
  twoFARecoveryGenerate: () =>
    request<{ codes: string[] }>("POST", "/v1/auth/2fa/recovery/generate"),
  twoFARecoveryRedeem: (partial_token: string, code: string) =>
    request<TokenPair>("POST", "/v1/auth/2fa/recovery", { partial_token, code }),

  // ----- browser-side keypair (replaces office-agent enrollment)
  keypairStatus: () =>
    request<{ enrolled: boolean; signed: boolean; ready: boolean }>(
      "GET",
      "/v1/auth/keypair/status",
    ),
  keypair: () =>
    request<{
      office_public_key: string | null;
      wrapped_o_sk_b64: string | null;
      cert_blob: string | null;
    }>("GET", "/v1/auth/keypair"),
  keypairEnroll: (office_public_key: string, wrapped_o_sk_b64: string) =>
    request<{
      office_public_key: string;
      wrapped_o_sk_b64: string;
      cert_blob: string | null;
    }>("POST", "/v1/auth/keypair/enroll", {
      office_public_key,
      wrapped_o_sk_b64,
    }),
  keypairPassphraseRotate: (
    office_public_key: string,
    wrapped_o_sk_b64: string,
  ) =>
    request<{
      office_public_key: string;
      wrapped_o_sk_b64: string;
      cert_blob: string | null;
    }>("POST", "/v1/auth/keypair/passphrase-rotate", {
      office_public_key,
      wrapped_o_sk_b64,
    }),

  // ----- dashboard file pulls (replaces /v1/agent/files/*)
  dashboardFilesPending: () =>
    request<{
      files: {
        file_id: string;
        size: number;
        ciphertext_sha256: string;
        document_label: string | null;
        resolved_path: string;
        resolved_filename: string;
      }[];
    }>("GET", "/v1/dashboard/files/pending"),
  dashboardFileAck: (file_id: string, plaintext_blake2b: string) =>
    request<{ ok: boolean }>("POST", `/v1/dashboard/files/${file_id}/ack`, {
      plaintext_blake2b,
    }),

  // ----- run matrix + reminders (day-of-work UX, plan §3.2)
  runMatrix: (run_id: string) =>
    request<{
      run_id: string;
      period_label: string;
      deadline_at: string;
      document_labels: string[];
      rows: {
        client_id: string;
        client_name: string;
        cells: {
          document_label: string;
          status: "acked" | "received" | "archived" | "missing";
          file_id: string | null;
          uploaded_at: string | null;
        }[];
        extra_docs: string[];
      }[];
      summary: { received: number; missing: number; clients: number };
    }>("GET", `/v1/runs/${run_id}/matrix`),
  runReminders: (run_id: string) =>
    request<{
      run_id: string;
      items: {
        client_id: string;
        client_name: string | null;
        tier: number;
        channel: string;
        sent_at: string;
      }[];
    }>("GET", `/v1/runs/${run_id}/reminders`),
  runReminderSend: (run_id: string, client_id: string, tier = 1, channel = "whatsapp") =>
    request<{ ok: boolean; note: string }>(
      "POST",
      `/v1/runs/${run_id}/reminders/send`,
      { client_id, tier, channel },
    ),

  // ----- clients search + last-upload column
  clientsListWithLastUpload: (q?: string) => {
    const qp = q ? `?q=${encodeURIComponent(q)}` : "";
    return request<{
      clients: {
        client_id: string;
        name: string;
        phone_e164: string | null;
        email: string | null;
        last_upload_at: string | null;
      }[];
    }>("GET", `/v1/clients_with_last_upload${qp}`);
  },

  // ----- migrating-from-spreadsheet UX
  bulkClientCreate: (
    clients: { name: string; phone_e164: string | null; email?: string | null }[],
  ) =>
    request<{
      created: ClientResponse[];
      failures: { name: string; reason: string }[];
    }>("POST", "/v1/clients/bulk", { clients }),
  clientSetRemindersPaused: (client_id: string, paused: boolean) =>
    request<ClientResponse>(
      "PATCH",
      `/v1/clients/${client_id}/reminders_paused`,
      { paused },
    ),
  // CSV is a Response, not JSON — we let the browser handle the download
  // via a regular <a href> with the bearer token in the URL would leak it
  // into history. Instead, fetch it ourselves and trigger an object URL.
  runExportCsvUrl: (run_id: string) => {
    const token = auth.getToken();
    return fetch(`${API_BASE}/v1/runs/${run_id}/export.csv`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "omit",
    })
      .then(async (r) => {
        if (!r.ok) throw new ApiError(r.status, `http_${r.status}`);
        return r.blob();
      })
      .then((blob) => URL.createObjectURL(blob));
  },
};
